import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, RefreshCw, ChevronRight, AlertTriangle, 
  Lock, Plus, X, Users, Clock, TrendingUp, Info
} from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors matching the design spec
const statusColors = {
  available: 'bg-emerald-500/20 border-emerald-500 text-emerald-400',
  warning: 'bg-amber-500/20 border-amber-500 text-amber-400',
  critical: 'bg-red-500/20 border-red-500 text-red-400',
  blocked: 'bg-slate-600/50 border-slate-500 text-slate-400',
};

const statusBgColors = {
  available: 'bg-emerald-500',
  warning: 'bg-amber-500',
  critical: 'bg-red-500',
  blocked: 'bg-slate-500',
};

// Format week date for display
const formatWeekLabel = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Territory Summary Card Component
const TerritorySummaryCard = ({ market, onClick }) => {
  const thisWeek = market.weeks?.[0] || {};
  const nextWeeksPreview = market.weeks?.slice(0, 3) || [];
  
  return (
    <div 
      onClick={() => onClick(market)}
      className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 cursor-pointer hover:border-slate-500 transition-all"
      data-testid={`territory-card-${market.market_id}`}
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-semibold text-white truncate">{market.market_name}</h3>
        <div className={`w-3 h-3 rounded-full ${statusBgColors[thisWeek.status] || 'bg-slate-500'}`} />
      </div>
      <div className="text-3xl font-bold text-white mb-1">
        {thisWeek.available_slots ?? 0}
      </div>
      <div className="text-sm text-slate-400 mb-3">slots this week</div>
      <div className="flex gap-1">
        {nextWeeksPreview.map((week, idx) => (
          <div 
            key={idx}
            className={`flex-1 h-1.5 rounded ${statusBgColors[week.status] || 'bg-slate-600'}`}
          />
        ))}
      </div>
      <div className="text-xs text-slate-500 mt-1">
        {market.crews_count} crew{market.crews_count !== 1 ? 's' : ''} • {market.base_weekly_slots} base slots/wk
      </div>
    </div>
  );
};

// Capacity Cell Component
const CapacityCell = ({ week, userRole, onClick }) => {
  const isBlocked = week.status === 'blocked' || week.available_slots <= 0;
  const isSalesRole = ['sales', 'sales_rep', 'sales_manager'].includes(userRole?.toLowerCase());
  const showLock = isBlocked && isSalesRole;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            onClick={() => onClick(week)}
            className={`
              p-3 rounded-lg border cursor-pointer transition-all hover:scale-[1.02]
              ${statusColors[week.status] || statusColors.available}
              ${isBlocked && isSalesRole ? 'opacity-60' : ''}
            `}
            data-testid={`capacity-cell-${week.week_start_date}`}
          >
            <div className="flex items-center gap-1 mb-1">
              {showLock && <Lock className="w-3 h-3" />}
              <span className="text-2xl font-bold">{week.available_slots}</span>
            </div>
            <div className="text-xs opacity-80">
              {week.reserved_slots}/{week.total_slots}
            </div>
            {week.has_override && (
              <div className="text-xs mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                <span className="truncate">{week.override_reason?.split(':')[0]}</span>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{week.available_slots} of {week.total_slots} slots available</p>
          <p className="text-xs opacity-70">{Math.round(week.utilization_percent)}% utilized</p>
          {isBlocked && isSalesRole && (
            <p className="text-xs text-amber-400 mt-1">Week is full. Contact PM to override.</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Override Modal Component
const OverrideModal = ({ open, onClose, markets, selectedMarket, selectedWeek, onSubmit }) => {
  const [formData, setFormData] = useState({
    market_id: selectedMarket?.market_id || '',
    week_start_date: selectedWeek?.week_start_date || '',
    delta_slots: '',
    reason: '',
    reason_notes: ''
  });
  
  useEffect(() => {
    if (selectedMarket) {
      setFormData(prev => ({ ...prev, market_id: selectedMarket.market_id }));
    }
    if (selectedWeek) {
      setFormData(prev => ({ ...prev, week_start_date: selectedWeek.week_start_date }));
    }
  }, [selectedMarket, selectedWeek]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      delta_slots: parseInt(formData.delta_slots, 10)
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white">Add Capacity Override</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-slate-400 block mb-1">Territory</label>
            <Select 
              value={formData.market_id} 
              onValueChange={(v) => setFormData(p => ({ ...p, market_id: v }))}
            >
              <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="override-market-select">
                <SelectValue placeholder="Select territory" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Markets</SelectItem>
                {markets?.map(m => (
                  <SelectItem key={m.market_id} value={m.market_id}>{m.market_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm text-slate-400 block mb-1">Week Starting</label>
            <Input 
              type="date" 
              value={formData.week_start_date}
              onChange={(e) => setFormData(p => ({ ...p, week_start_date: e.target.value }))}
              className="bg-slate-700 border-slate-600"
              data-testid="override-week-input"
            />
          </div>
          
          <div>
            <label className="text-sm text-slate-400 block mb-1">Slot Adjustment (+/-)</label>
            <Input 
              type="number"
              placeholder="-3 or +5"
              value={formData.delta_slots}
              onChange={(e) => setFormData(p => ({ ...p, delta_slots: e.target.value }))}
              className="bg-slate-700 border-slate-600"
              data-testid="override-delta-input"
            />
          </div>
          
          <div>
            <label className="text-sm text-slate-400 block mb-1">Reason *</label>
            <Select 
              value={formData.reason} 
              onValueChange={(v) => setFormData(p => ({ ...p, reason: v }))}
            >
              <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="override-reason-select">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weather">Weather</SelectItem>
                <SelectItem value="holiday">Holiday</SelectItem>
                <SelectItem value="crew_down">Crew Down</SelectItem>
                <SelectItem value="surge">Surge Capacity</SelectItem>
                <SelectItem value="pm_override">PM Override</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm text-slate-400 block mb-1">Notes</label>
            <Textarea 
              placeholder="Additional details..."
              value={formData.reason_notes}
              onChange={(e) => setFormData(p => ({ ...p, reason_notes: e.target.value }))}
              className="bg-slate-700 border-slate-600"
              data-testid="override-notes-input"
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button 
              type="submit" 
              disabled={!formData.market_id || !formData.week_start_date || !formData.delta_slots || !formData.reason}
              data-testid="override-submit-btn"
            >
              Create Override
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Capacity Dashboard Component
export default function CapacityDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [capacityData, setCapacityData] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [weeksToShow, setWeeksToShow] = useState('8');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedMarketData, setSelectedMarketData] = useState(null);
  
  // Override modal state
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideMarket, setOverrideMarket] = useState(null);
  const [overrideWeek, setOverrideWeek] = useState(null);
  
  // User info
  const [userRole, setUserRole] = useState('');
  
  const canManageOverrides = ['project_manager', 'operations_manager', 'admin', 'super_admin'].includes(userRole?.toLowerCase());
  
  // Fetch capacity data
  const fetchCapacityData = useCallback(async () => {
    try {
      setIsPolling(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const params = new URLSearchParams({
        weeks: weeksToShow,
        include_jobs: 'true'
      });
      if (selectedMarket && selectedMarket !== 'all') {
        params.append('market_id', selectedMarket);
      }
      
      const res = await fetch(`${API_URL}/api/ops/capacity/weekly?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch capacity data');
      
      const data = await res.json();
      setCapacityData(data);
      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('Capacity fetch error:', err);
      toast({
        title: 'Error',
        description: 'Failed to load capacity data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
      setIsPolling(false);
    }
  }, [weeksToShow, selectedMarket, toast]);
  
  // Get user role on mount
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const user = await res.json();
          setUserRole(user.role);
        }
      } catch (err) {
        console.error('Failed to fetch user:', err);
      }
    };
    fetchUser();
  }, []);
  
  // Initial fetch
  useEffect(() => {
    fetchCapacityData();
  }, [fetchCapacityData]);
  
  // Polling every 60 seconds
  useEffect(() => {
    const interval = setInterval(fetchCapacityData, 60000);
    return () => clearInterval(interval);
  }, [fetchCapacityData]);
  
  // Handle cell click
  const handleCellClick = (market, week) => {
    setSelectedMarketData(market);
    setSelectedCell(week);
    setDrawerOpen(true);
  };
  
  // Handle territory card click
  const handleTerritoryClick = (market) => {
    setSelectedMarket(market.market_id);
  };
  
  // Handle add override
  const handleAddOverride = (market, week) => {
    setOverrideMarket(market);
    setOverrideWeek(week);
    setOverrideModalOpen(true);
  };
  
  // Submit override
  const handleOverrideSubmit = async (data) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ops/capacity/overrides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create override');
      }
      
      toast({
        title: 'Override Created',
        description: `Capacity adjusted by ${data.delta_slots > 0 ? '+' : ''}${data.delta_slots} slots`
      });
      
      setOverrideModalOpen(false);
      fetchCapacityData();
      
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive'
      });
    }
  };
  
  // Get unique week headers from first market
  const weekHeaders = capacityData?.markets?.[0]?.weeks?.map(w => ({
    label: formatWeekLabel(w.week_start_date),
    date: w.week_start_date
  })) || [];
  
  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-slate-900 p-6" data-testid="capacity-dashboard-loading">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-16 bg-slate-800 rounded-lg" />
              <div className="grid grid-cols-5 gap-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-32 bg-slate-800 rounded-lg" />
                ))}
              </div>
              <div className="h-96 bg-slate-800 rounded-lg" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="min-h-screen bg-slate-900 text-white" data-testid="capacity-dashboard">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur border-b border-slate-800 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-blue-500" />
              <h1 className="text-xl font-bold">Capacity & Scheduling</h1>
            </div>
          
          <div className="flex items-center gap-4">
            {/* Week range selector */}
            <Select value={weeksToShow} onValueChange={setWeeksToShow}>
              <SelectTrigger className="w-28 bg-slate-800 border-slate-700" data-testid="weeks-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4 weeks</SelectItem>
                <SelectItem value="8">8 weeks</SelectItem>
                <SelectItem value="12">12 weeks</SelectItem>
                <SelectItem value="16">16 weeks</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Territory filter */}
            <Select value={selectedMarket} onValueChange={setSelectedMarket}>
              <SelectTrigger className="w-44 bg-slate-800 border-slate-700" data-testid="market-filter">
                <SelectValue placeholder="All Territories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Territories</SelectItem>
                {capacityData?.markets?.map(m => (
                  <SelectItem key={m.market_id} value={m.market_id}>{m.market_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Refresh button */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={fetchCapacityData}
              className={isPolling ? 'animate-spin' : ''}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            
            {/* Last updated */}
            {lastUpdated && (
              <span className="text-xs text-slate-500">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Company totals banner */}
        {capacityData?.company_totals && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-sm text-slate-400">Total Available</div>
                <div className="text-2xl font-bold text-emerald-400">
                  {capacityData.company_totals.available_slots} slots
                </div>
              </div>
              <div className="h-10 w-px bg-slate-700" />
              <div>
                <div className="text-sm text-slate-400">Utilization</div>
                <div className="text-2xl font-bold">
                  {capacityData.company_totals.utilization_percent}%
                </div>
              </div>
              <div className="h-10 w-px bg-slate-700" />
              <div>
                <div className="text-sm text-slate-400">Period</div>
                <div className="text-lg">
                  {formatWeekLabel(capacityData.period.start_date)} - {formatWeekLabel(capacityData.period.end_date)}
                </div>
              </div>
            </div>
            
            {canManageOverrides && (
              <Button 
                onClick={() => setOverrideModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-override-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Override
              </Button>
            )}
          </div>
        )}
        
        {/* Territory Summary Strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {capacityData?.markets?.map(market => (
            <TerritorySummaryCard 
              key={market.market_id}
              market={market}
              onClick={handleTerritoryClick}
            />
          ))}
        </div>
        
        {/* Weekly Capacity Grid */}
        <div className="bg-slate-800/30 border border-slate-700 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="sticky left-0 bg-slate-800 px-4 py-3 text-left text-sm font-semibold text-slate-300 w-40">
                    Territory
                  </th>
                  {weekHeaders.map((week, idx) => (
                    <th key={idx} className="px-2 py-3 text-center text-sm font-semibold text-slate-300">
                      <div>{week.label}</div>
                      <div className="text-xs text-slate-500 font-normal">
                        Week {new Date(week.date + 'T00:00:00').toLocaleDateString('en-US', { day: '2-digit' })}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {capacityData?.markets?.map(market => (
                  <tr key={market.market_id} className="border-b border-slate-700/50">
                    <td className="sticky left-0 bg-slate-800/80 px-4 py-3">
                      <div className="font-medium text-white">{market.market_name}</div>
                      <div className="text-xs text-slate-500">
                        {market.crews_count} crews • {market.base_weekly_slots}/wk
                      </div>
                    </td>
                    {market.weeks?.map((week, idx) => (
                      <td key={idx} className="px-2 py-2">
                        <CapacityCell 
                          week={week}
                          userRole={userRole}
                          onClick={() => handleCellClick(market, week)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* No data state */}
        {(!capacityData?.markets || capacityData.markets.length === 0) && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Capacity Data</h3>
            <p className="text-slate-400 mb-4">
              No crews or markets are configured. Set up crews to track capacity.
            </p>
            <Button onClick={() => navigate('/operations-hub/crew')}>
              Go to Crew Management
            </Button>
          </div>
        )}
      </div>
      
      {/* Drilldown Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-[540px] bg-slate-800 border-slate-700">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center justify-between">
              <span>{selectedMarketData?.market_name} - Week of {formatWeekLabel(selectedCell?.week_start_date || '')}</span>
            </SheetTitle>
          </SheetHeader>
          
          {selectedCell && (
            <div className="mt-6 space-y-6">
              {/* Capacity Breakdown */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Capacity Breakdown
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">{selectedMarketData?.crews_count} crews × {Math.round(selectedMarketData?.base_weekly_slots / selectedMarketData?.crews_count)} slots/wk</span>
                    <span className="text-white">{selectedCell.base_slots} base</span>
                  </div>
                  {selectedCell.override_delta !== 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Override ({selectedCell.override_reason?.split(':')[0]})</span>
                      <span className={selectedCell.override_delta > 0 ? 'text-emerald-400' : 'text-red-400'}>
                        {selectedCell.override_delta > 0 ? '+' : ''}{selectedCell.override_delta}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-slate-600 pt-2 flex justify-between">
                    <span className="text-slate-400">Total Slots</span>
                    <span className="text-white font-semibold">{selectedCell.total_slots}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Reserved</span>
                    <span className="text-white">{selectedCell.reserved_slots}</span>
                  </div>
                  <div className="border-t border-slate-600 pt-2 flex justify-between text-lg">
                    <span className="font-semibold">Available</span>
                    <span className={`font-bold ${selectedCell.available_slots > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {selectedCell.available_slots}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Scheduled Jobs */}
              {selectedCell.jobs && selectedCell.jobs.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Scheduled Jobs ({selectedCell.jobs.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedCell.jobs.map((job, idx) => (
                      <div key={idx} className="bg-slate-700/50 rounded p-3">
                        <div className="font-medium text-white">{job.customer_name}</div>
                        <div className="text-sm text-slate-400">
                          {new Date(job.scheduled_start).toLocaleDateString()} • {job.status}
                        </div>
                        {job.assigned_crew && (
                          <div className="text-xs text-slate-500 mt-1">{job.assigned_crew}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              {canManageOverrides && (
                <div className="pt-4 border-t border-slate-700">
                  <Button 
                    onClick={() => handleAddOverride(selectedMarketData, selectedCell)}
                    className="w-full"
                    data-testid="drawer-add-override-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Override for This Week
                  </Button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
      
      {/* Override Modal */}
      <OverrideModal 
        open={overrideModalOpen}
        onClose={() => setOverrideModalOpen(false)}
        markets={capacityData?.markets || []}
        selectedMarket={overrideMarket}
        selectedWeek={overrideWeek}
        onSubmit={handleOverrideSubmit}
      />
      </div>
    </Layout>
  );
}
